import { useEffect, useRef, type Dispatch, type SetStateAction } from "react";
import { needsFreshReadURL } from "../domain/repository";
import type { ReOSDatabase, StoredAsset } from "../domain/types";

type Refresh = (database: ReOSDatabase, forced: string[]) => Promise<ReOSDatabase>;
const sameAsset = (a: StoredAsset, b: StoredAsset) => a.id === b.id && a.workspaceID === b.workspaceID && a.storageKey === b.storageKey && a.updatedAt === b.updatedAt && a.checksum === b.checksum && !a.cleanupPendingAt;

/** Merge only read links; an in-flight renewal must never revert a user's edits. */
export function mergePhotoReadURLs(current: ReOSDatabase, before: ReOSDatabase, loaded: ReOSDatabase) {
  if (current.currentUserID !== before.currentUserID || current.activeWorkspaceID !== before.activeWorkspaceID) return current;
  const originals = new Map(before.storedAssets.map((asset) => [asset.id, asset]));
  const renewed = new Map(loaded.storedAssets.filter((asset) => { const before = originals.get(asset.id); return asset.readURL && !needsFreshReadURL(asset) && (asset.readURL !== before?.readURL || asset.signedURLExpiresAt !== before?.signedURLExpiresAt); }).map((asset) => [asset.id, asset]));
  const urls = new Map<string, string>();
  const storedAssets = current.storedAssets.map((asset) => {
    const original = originals.get(asset.id), fresh = renewed.get(asset.id);
    if (!original || !fresh || !sameAsset(asset, original) || !sameAsset(asset, fresh)) return asset;
    if (Date.parse(asset.signedURLExpiresAt ?? "") > Date.parse(fresh.signedURLExpiresAt ?? "")) return asset;
    urls.set(asset.id, fresh.readURL!);
    return { ...asset, readURL: fresh.readURL, signedURLExpiresAt: fresh.signedURLExpiresAt };
  });
  if (!urls.size) return current;
  return { ...current, storedAssets, itemPhotos: current.itemPhotos.map((photo) => {
    const url = photo.assetID && urls.get(photo.assetID);
    return url ? { ...photo, url, thumbnailURL: url } : photo;
  }) };
}

/** Private image links expire independently of releases. Renew while open and on return. */
export function usePhotoReadRefresh(database: ReOSDatabase, setDatabase: Dispatch<SetStateAction<ReOSDatabase>>, refresh: Refresh | undefined) {
  const latest = useRef({ database, refresh }); latest.current = { database, refresh };
  const workspaceID = database.activeWorkspaceID, userID = database.currentUserID;
  const enabled = Boolean(refresh);
  useEffect(() => {
    if (!enabled || !workspaceID || !userID) return;
    let active = true, busy = false, failures = 0;
    let timer: number | undefined;
    const forced = new Set<string>();
    const retriedURLs = new Set<string>();
    const lastImageRetry = new Map<string, number>();
    const assets = () => latest.current.database.storedAssets.filter((asset) => asset.workspaceID === workspaceID && !asset.cleanupPendingAt && !asset.publicURL);
    const schedule = (ms = 30_000) => { window.clearTimeout(timer); if (active) timer = window.setTimeout(run, ms); };
    async function run() {
      if (!active || busy) return;
      if (document.visibilityState === "hidden" || !navigator.onLine) { schedule(); return; }
      if (!forced.size && !assets().some(needsFreshReadURL)) { schedule(); return; }
      busy = true;
      const before = latest.current.database, requested = [...forced]; forced.clear();
      try {
        const loaded = await latest.current.refresh!(before, requested);
        if (!active) return;
        requested.forEach((id) => { if (loaded.storedAssets.some((asset) => asset.id === id && needsFreshReadURL(asset))) forced.add(id); });
        setDatabase((current) => mergePhotoReadURLs(current, before, loaded));
        const remaining = loaded.storedAssets.some((asset) => asset.workspaceID === workspaceID && !asset.cleanupPendingAt && !asset.publicURL && needsFreshReadURL(asset));
        failures = remaining ? Math.min(failures + 1, 5) : 0;
      } catch { requested.forEach((id) => forced.add(id)); failures = Math.min(failures + 1, 5); }
      finally { busy = false; schedule(failures ? Math.min(60_000, 2000 * 2 ** failures) : 30_000); }
    }
    const wake = () => { if (document.visibilityState !== "hidden") void run(); };
    // Captures errors from any gallery without making every photo component own a poller.
    const imageFailed = (event: Event) => {
      const image = event.target;
      if (!(image instanceof HTMLImageElement) || retriedURLs.has(image.src)) return;
      const asset = assets().find((candidate) => candidate.readURL === image.src);
      if (!asset) return;
      retriedURLs.add(image.src); forced.add(asset.id);
      const delay = Math.max(250, 30_000 - (Date.now() - (lastImageRetry.get(asset.id) ?? 0)));
      lastImageRetry.set(asset.id, Date.now() + delay); schedule(delay);
    };
    schedule(2000);
    window.addEventListener("focus", wake); window.addEventListener("online", wake);
    document.addEventListener("visibilitychange", wake); document.addEventListener("error", imageFailed, true);
    return () => { active = false; window.clearTimeout(timer); window.removeEventListener("focus", wake); window.removeEventListener("online", wake); document.removeEventListener("visibilitychange", wake); document.removeEventListener("error", imageFailed, true); };
  }, [enabled, workspaceID, userID, setDatabase]);
}
